import { useState, useEffect } from "react";
import { getDirectionFromTiles } from "../utils/getDirectionFromTiles";
import { positionEquals } from "../utils/equalCheckers";
import { fetchByCoords, getWordFromSelection } from "../utils/getWordFromSelection";
import { SOPA_SOUNDS } from "../utils/consts";
import { useGameSounds } from "@educaplay/core";

/**
 * Hook para manejar los controles de teclado del juego
 */
export const useKeyboardControls = ({
  tiles,
  gridSize,
  pointerUpHandler,
  tileRefs,
  chainToSelector,
  selectorCheck,
  words
}) => {
  const [keyboardChain, setKeyboardChain] = useState([]);
  const [navigationMode, setNavigationMode] = useState(false);
  const gameSounds = useGameSounds();

  useEffect(() => {
    const moveToNextInDirection = (tile, direction) => {
      const newPos = {
        x: tile.position.x + direction.x,
        y: tile.position.y + direction.y,
      };

      if (newPos.x < 0 || newPos.x >= gridSize || newPos.y < 0 || newPos.y >= gridSize) {
        return;
      }

      const nextTileId = newPos.y * gridSize + newPos.x;
      if (tileRefs?.current && tileRefs.current[nextTileId]) {
        tileRefs.current[nextTileId].focus();
        gameSounds.play(SOPA_SOUNDS.MOUSE_HOVER);
      }
    };

    const softCheck = (keyboardData, { chainToSelector, selectorCheck, words }) => {
      const selectorLocal = chainToSelector(keyboardData);
      selectorLocal.word = getWordFromSelection(selectorLocal, tiles, gridSize);

      if (!selectorLocal.word) return false;

      selectorLocal.enunId = selectorCheck(words, selectorLocal);
      if (selectorLocal.enunId !== -1) {
        pointerUpHandler(keyboardData);
        setKeyboardChain([]);
        return true;
      }
      return false;
    };

    const handleGlobalKeyDown = (evt) => {
      if (evt.key === "Escape" || evt.key === "Backspace") {
        evt.preventDefault();
        evt.stopPropagation();
        setNavigationMode(false);
        setKeyboardChain([]);
        return;
      }

      if (!navigationMode && (evt.key === "Enter" || evt.key === " ")) {
        evt.preventDefault();
        evt.stopPropagation();
        setNavigationMode(true);
        gameSounds.play(SOPA_SOUNDS.MOUSE_HOVER);

        if (tileRefs?.current && tileRefs.current[0]) {
          tileRefs.current[0].focus();
        }
        return;
      }

      if (navigationMode) {
        if (evt.key.startsWith("Arrow")) {
          evt.preventDefault();
          evt.stopPropagation();

          const currentFocused = document.activeElement;
          if (!currentFocused || !tileRefs?.current) return;

          const currentTileId = Object.keys(tileRefs.current).find(
            id => tileRefs.current[id] === currentFocused
          );

          if (currentTileId !== undefined) {
            const currentTile = tiles[currentTileId];
            let direction;

            switch (evt.key) {
              case "ArrowUp": direction = { x: 0, y: -1 }; break;
              case "ArrowDown": direction = { x: 0, y: 1 }; break;
              case "ArrowLeft": direction = { x: -1, y: 0 }; break;
              case "ArrowRight": direction = { x: 1, y: 0 }; break;
              default: return;
            }

            const newPos = {
              x: currentTile.position.x + direction.x,
              y: currentTile.position.y + direction.y,
            };

            const wrappedPos = {
              x: ((newPos.x % gridSize) + gridSize) % gridSize, 
              y: ((newPos.y % gridSize) + gridSize) % gridSize  
            };

            const newTileId = wrappedPos.y * gridSize + wrappedPos.x;
            if (tileRefs.current[newTileId]) {
              tileRefs.current[newTileId].focus();
              gameSounds.play(SOPA_SOUNDS.MOUSE_HOVER);
            }
          }
          return;
        }

        if (evt.key === "Enter" || evt.key === " ") {
          evt.preventDefault();
          evt.stopPropagation();

          const currentFocused = document.activeElement;
          if (!currentFocused || !tileRefs?.current) return;

          const currentTileId = Object.keys(tileRefs.current).find(
            id => tileRefs.current[id] === currentFocused
          );

          if (currentTileId !== undefined) {
            const currentTile = tiles[currentTileId];

            if (keyboardChain.length === 0) {
              setKeyboardChain([currentTile]);
              gameSounds.play(SOPA_SOUNDS.CARGAR_LETRA);
              return;
            }

            const chainIndex = keyboardChain.findIndex((it) =>
              positionEquals(it.position, currentTile.position)
            );

            if (chainIndex === 0) {
              setKeyboardChain([]);
              gameSounds.play(SOPA_SOUNDS.CARGAR_LETRA);
              return;
            }

            if (chainIndex > 0) {
              setKeyboardChain((old) => old.slice(0, chainIndex));
              gameSounds.play(SOPA_SOUNDS.CARGAR_LETRA);
              return;
            }

            const requestedDirection = getDirectionFromTiles(keyboardChain[0], currentTile);
            const newChain = [];
            try {
              let actual = keyboardChain[0];
              while (!positionEquals(actual.position, currentTile.position)) {
                newChain.push(actual);
                actual =
                  tiles[
                  (actual.position.y + requestedDirection.y) * gridSize +
                  (actual.position.x + requestedDirection.x)
                  ];
              }
              newChain.push(currentTile);
            } catch (e) {
              newChain.splice(0);
            }

            if (newChain.length === 0) {
              if (keyboardChain.length > 1) {
                pointerUpHandler(keyboardChain);
              }
              setKeyboardChain([]);
              return;
            }

            if (keyboardChain.length === 1) {
              setKeyboardChain(newChain);
              gameSounds.play(SOPA_SOUNDS.CARGAR_LETRA);
              if (softCheck(newChain, { chainToSelector, selectorCheck, words })) return;
              moveToNextInDirection(newChain[newChain.length - 1], requestedDirection);
              return;
            }

            if (newChain.length === 2 && keyboardChain.length === 2) {
              setKeyboardChain(newChain);
              gameSounds.play(SOPA_SOUNDS.CARGAR_LETRA);
              if (softCheck(newChain, { chainToSelector, selectorCheck, words })) return;
              moveToNextInDirection(newChain[newChain.length - 1], requestedDirection);
            } else {
              const expectedDirection = getDirectionFromTiles(
                keyboardChain[0],
                keyboardChain[1]
              );
              if (positionEquals(expectedDirection, requestedDirection)) {
                setKeyboardChain(newChain);
                gameSounds.play(SOPA_SOUNDS.CARGAR_LETRA);
                if (softCheck(newChain, { chainToSelector, selectorCheck, words })) return;
                moveToNextInDirection(newChain[newChain.length - 1], requestedDirection);
              } else {
                pointerUpHandler(keyboardChain);
                setKeyboardChain([]);
              }
            }
          }
          return;
        }
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown);

    return () => {
      document.removeEventListener("keydown", handleGlobalKeyDown);
    };
  }, [navigationMode, tileRefs, tiles, gridSize, keyboardChain, pointerUpHandler, chainToSelector, selectorCheck, words, gameSounds]);

  const softCheck = (keyboardData, { chainToSelector, selectorCheck, words }) => {
    const selectorLocal = chainToSelector(keyboardData);
    selectorLocal.word = getWordFromSelection(selectorLocal, tiles, gridSize);

    if (!selectorLocal.word) return false;

    selectorLocal.enunId = selectorCheck(words, selectorLocal);
    if (selectorLocal.enunId !== -1) {
      pointerUpHandler(keyboardData);
      fullCancelHandler();
      return true;
    }
    return false;
  };

  const selectHandler = (tile, { chainToSelector, selectorCheck, words }) => {
    if (navigationMode && keyboardChain.length === 0) {
      setKeyboardChain([tile]);
      gameSounds.play(SOPA_SOUNDS.CARGAR_LETRA);
      return;
    }

    const chainIndex = keyboardChain.findIndex((it) =>
      positionEquals(it.position, tile.position)
    );

    if (chainIndex === 0) {
      cancelHandler();
      return;
    }

    if (chainIndex > 0) {
      setKeyboardChain((old) => old.slice(0, chainIndex));
      gameSounds.play(SOPA_SOUNDS.CARGAR_LETRA);
      return;
    }

    const requestedDirection = getDirectionFromTiles(keyboardChain[0], tile);
    const newChain = [];
    try {
      let actual = keyboardChain[0];
      while (!positionEquals(actual.position, tile.position)) {
        newChain.push(actual);
        actual =
          tiles[
          (actual.position.y + requestedDirection.y) * gridSize +
          (actual.position.x + requestedDirection.x)
          ];
      }
      newChain.push(tile);
    } catch (e) {
      newChain.splice(0);
    }

    if (newChain.length === 0) {
      if (keyboardChain.length > 1) {
        pointerUpHandler(keyboardChain);
      }
      cancelHandler();
      return;
    }

    if (keyboardChain.length === 1) {
      setKeyboardChain(newChain);
      if (softCheck(newChain, { chainToSelector, selectorCheck, words })) return;
      arrowHandler(newChain[newChain.length - 1], requestedDirection);
      return;
    }

    if (newChain.length === 2 && keyboardChain.length === 2) {
      setKeyboardChain(newChain);
      if (softCheck(newChain, { chainToSelector, selectorCheck, words })) return;
      arrowHandler(newChain[newChain.length - 1], requestedDirection);
    } else {
      const expectedDirection = getDirectionFromTiles(
        keyboardChain[0],
        keyboardChain[1]
      );
      if (expectedDirection === requestedDirection) {
        setKeyboardChain(newChain);
        if (softCheck(newChain, { chainToSelector, selectorCheck, words })) return;
        arrowHandler(newChain[newChain.length - 1], requestedDirection);
      } else {
        pointerUpHandler(keyboardChain);
        cancelHandler();
      }
    }
  };

  const arrowHandler = (tile, direction, tileRefs) => {
    const newPos = {
      x: tile.position.x + direction.x,
      y: tile.position.y + direction.y,
    };

    if (newPos.x < 0 || newPos.x >= gridSize || newPos.y < 0 || newPos.y >= gridSize) {
      return;
    }

    const tileRef = fetchByCoords(newPos, tileRefs.current, gridSize);
    if (tileRef && typeof tileRef.focus === 'function') {
      tileRef.focus();
    }
  };

  const fullCancelHandler = () => {
    setKeyboardChain([]);
    setNavigationMode(false);
  };

  const cancelHandler = () => {
    setKeyboardChain([]);
    gameSounds.play(SOPA_SOUNDS.CARGAR_LETRA);
  };

  const cancelKeyboardHandler = () => {
    setKeyboardChain([]);
    setNavigationMode(false);
  };

  return {
    keyboardChain,
    navigationMode,
    setKeyboardChain,
    selectHandler,
    arrowHandler,
    cancelHandler,
    cancelKeyboardHandler
  };
};
